import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type * as Cesium from 'cesium';
import type { SatApp } from '../App';
import {
  ALTITUDE_PRESETS,
  chooseZoomAction,
  ecefDistanceMeters,
  findClosestAltitudePreset,
  flyToAltitude,
  flyToCloseUpTracking,
  flyToContextTracking,
  type AltitudePreset,
  type ZoomAction,
} from '../globe/ZoomController';
import { findAncestorAcrossShadow } from '../util/shadowDom';

/**
 * Phase 9 chunk 1 — `<sat-zoom-button>` topbar element.
 *
 * Single context-aware button with three states (locked in
 * docs/phase9.md § II row 1):
 *   - `'in-no-sel'`   (icon ⊕): no selection → fly to a 1,500 km
 *                                close-up over the current camera
 *                                ground-track point
 *   - `'out-context'` (icon ⊖): satellite selected + camera ≤ 5,000 km
 *                                from it → fly out to 10,000 km above
 *                                the sat to see the constellation
 *   - `'recenter'`    (icon ⊙): satellite selected + camera elsewhere
 *                                → fly back down to 800 km above the
 *                                sat for a tracking close-up
 *
 * Hidden in sky view (§ II row 10 — sky view has its own FOV-based
 * mouse-wheel zoom). Disabled in the `/conjunction/{p}/{s}` replay
 * route (§ II row 11 — the chase camera owns framing).
 *
 * Lives in the topbar between `<sat-share-button>` and
 * `<sat-view-toggle>` per § II row 3; collapses to icon-only below
 * 1300 px per the existing label-collapse breakpoint cascade.
 */
@customElement('sat-zoom-button')
export class SatZoomButton extends LitElement {
  @state() private selectedNorad: number | null = null;
  @state() private viewMode: 'globe' | 'sky' = 'globe';
  @state() private inReplayRoute = false;
  /** Live distance from camera to selected sat (ECEF meters); null when unknowable. */
  @state() private distanceM: number | null = null;
  /** True when the no-selection altitude-preset dropdown is open. */
  @state() private menuOpen = false;
  /** Current camera altitude above WGS84 (meters); drives the
   *  "closest preset" highlight in the dropdown. Null until the
   *  viewer has reported a finite cartographic height. */
  @state() private cameraAltitudeM: number | null = null;

  private viewModeListener = (e: Event): void => {
    const detail = (e as CustomEvent<{ viewMode: 'globe' | 'sky' }>).detail;
    if (detail && (detail.viewMode === 'globe' || detail.viewMode === 'sky')) {
      this.viewMode = detail.viewMode;
    }
  };

  private selectionListener = (e: Event): void => {
    const detail = (e as CustomEvent<{ norad: number | null }>).detail;
    if (detail !== undefined && detail !== null) {
      this.selectedNorad = detail.norad;
      this.refreshDistance();
    }
  };

  private removeCameraListener: (() => void) | null = null;
  /** Backoff timer that keeps polling the satellite worker until it reports a position. */
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('view-mode-changed', this.viewModeListener);
    window.addEventListener('selection-changed', this.selectionListener);
    document.addEventListener('click', this.handleOutsideClick);
    this.inReplayRoute = window.location.pathname.startsWith('/conjunction/');
    // Pick up the post-mount state in case the app initialized
    // before this element registered (URL-restored selection,
    // restored sky-view mode, etc.).
    const app = findAncestorAcrossShadow(this, 'sat-app') as SatApp | null;
    if (app !== null) {
      this.viewMode = app.getViewMode();
      this.selectedNorad = app.getSelectedNorad();
      this.attachCameraListener(app);
      this.refreshDistance();
      this.refreshCameraAltitude();
    }
    // Re-check distance + altitude on a 500 ms cadence while a sat
    // is selected and the position hasn't been reported yet (the
    // worker can take a moment to deliver the first ECEF after page
    // load) and so the altitude-preset highlight stays in sync if
    // the user dragwheel-zooms outside a flyTo.
    this.pollTimer = setInterval(() => {
      this.refreshDistance();
      this.refreshCameraAltitude();
    }, 500);
  }

  disconnectedCallback(): void {
    window.removeEventListener('view-mode-changed', this.viewModeListener);
    window.removeEventListener('selection-changed', this.selectionListener);
    document.removeEventListener('click', this.handleOutsideClick);
    this.removeCameraListener?.();
    this.removeCameraListener = null;
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    super.disconnectedCallback();
  }

  /** Close the altitude dropdown when the user clicks anywhere
   *  outside this element. Uses composedPath so a click inside the
   *  shadow root counts as "inside". */
  private handleOutsideClick = (e: MouseEvent): void => {
    if (!this.menuOpen) return;
    if (!e.composedPath().includes(this)) {
      this.menuOpen = false;
    }
  };

  private attachCameraListener(app: SatApp): void {
    const viewer = app.getGlobe()?.viewer;
    if (viewer === undefined) return;
    const onChange = (): void => { this.refreshDistance(); };
    viewer.camera.changed.addEventListener(onChange);
    this.removeCameraListener = () => viewer.camera.changed.removeEventListener(onChange);
  }

  private refreshDistance(): void {
    if (this.selectedNorad === null) {
      this.distanceM = null;
      return;
    }
    const app = findAncestorAcrossShadow(this, 'sat-app') as SatApp | null;
    const viewer = app?.getGlobe()?.viewer;
    const satPos = app?.getGlobe()?.layer?.getPosition(this.selectedNorad) ?? null;
    if (viewer === undefined || satPos === null) {
      this.distanceM = null;
      return;
    }
    this.distanceM = ecefDistanceMeters(viewer.camera.positionWC, satPos);
  }

  private refreshCameraAltitude(): void {
    const app = findAncestorAcrossShadow(this, 'sat-app') as SatApp | null;
    const viewer = app?.getGlobe()?.viewer;
    if (viewer === undefined) {
      this.cameraAltitudeM = null;
      return;
    }
    const h = viewer.camera.positionCartographic.height;
    this.cameraAltitudeM = Number.isFinite(h) ? h : null;
  }

  static styles = css`
    :host {
      position: relative;
      display: inline-block;
      font-family: var(--font-mono);
    }
    button {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.3rem 0.6rem;
      background: transparent;
      color: var(--color-text);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      font-family: var(--font-mono);
      font-size: 0.85rem;
      cursor: pointer;
      transition: color 0.1s ease, border-color 0.1s ease;
    }
    button:hover:not([disabled]) {
      border-color: var(--color-accent);
      color: var(--color-accent);
    }
    button[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .toggle__glyph { font-size: 1rem; line-height: 1; }

    /* Phase 8 chunk 1 — reduced-motion mirrors the rest of the
       topbar pills: keep the color change, drop the transition. */
    @media (prefers-reduced-motion: reduce) {
      button { transition: none; }
    }

    /* Collapse the label below 1300px to match the other action pills. */
    @media (max-width: 1300px) {
      .toggle__label { display: none; }
    }

    /* Altitude-preset dropdown (no-selection state).
       Mirrors <sat-overlays-menu>'s panel shape so the topbar
       reads cohesively. */
    .menu {
      position: absolute;
      top: calc(100% + 0.4rem);
      right: 0;
      min-width: 260px;
      background: var(--color-bg-elevated);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      padding: 0.4rem 0;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
      z-index: 100;
    }
    .menu h3 {
      margin: 0;
      padding: 0 0.8rem 0.4rem;
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--color-text-dim);
    }
    .item {
      display: flex;
      align-items: baseline;
      gap: 0.6rem;
      padding: 0.45rem 0.8rem;
      cursor: pointer;
      background: transparent;
      border: none;
      width: 100%;
      text-align: left;
      color: var(--color-text);
      font-family: var(--font-mono);
      font-size: inherit;
      transition: background 0.1s ease;
    }
    .item:hover { background: rgba(255, 255, 255, 0.04); }
    .item:focus-visible {
      outline: 1px solid var(--color-accent);
      outline-offset: -1px;
    }
    .item[data-active='true'] {
      color: var(--color-accent);
    }
    .item[data-active='true']::before {
      content: '▸';
      position: absolute;
      margin-left: -0.85rem;
      color: var(--color-accent);
    }
    .item__label {
      flex-shrink: 0;
      width: 3.5rem;
      font-weight: 500;
    }
    .item__desc {
      color: var(--color-text-dim);
      font-size: 0.75rem;
    }
  `;

  private currentAction(): ZoomAction {
    return chooseZoomAction(this.selectedNorad, this.distanceM);
  }

  private glyphFor(action: ZoomAction): string {
    switch (action) {
      case 'in-no-sel':   return '⊕';
      case 'out-context': return '⊖';
      case 'recenter':    return '⊙';
    }
  }

  private labelFor(action: ZoomAction): string {
    switch (action) {
      case 'in-no-sel':   return 'Zoom in';
      case 'out-context': return 'Zoom out';
      case 'recenter':    return 'Recenter';
    }
  }

  private tooltipFor(action: ZoomAction): string {
    if (this.inReplayRoute) return 'Zoom unavailable in replay mode';
    switch (action) {
      case 'in-no-sel':   return 'Pick an altitude — LEO, MEO, GEO, or a custom bookend';
      case 'out-context': return 'Pull out to 10,000 km to see this constellation';
      case 'recenter':    return 'Recenter on the selected satellite at 800 km';
    }
  }

  private handleClick(): void {
    if (this.inReplayRoute) return;
    const action = this.currentAction();
    if (action === 'in-no-sel') {
      // No selection → button is a dropdown trigger. The actual fly
      // happens in handleAltitudeClick once the user picks a preset.
      this.menuOpen = !this.menuOpen;
      return;
    }
    const app = findAncestorAcrossShadow(this, 'sat-app') as SatApp | null;
    const viewer = app?.getGlobe()?.viewer;
    if (app === null || viewer === undefined) return;
    // Need a live satellite position for the tracking actions.
    const satPos: Cesium.Cartesian3 | null = app.getGlobe()?.layer?.getPosition(this.selectedNorad ?? -1) ?? null;
    if (satPos === null) return; // worker hasn't reported yet — let the next poll handle it
    if (action === 'out-context') {
      flyToContextTracking(viewer, satPos);
    } else {
      flyToCloseUpTracking(viewer, satPos);
    }
  }

  private handleAltitudeClick(preset: AltitudePreset): void {
    const app = findAncestorAcrossShadow(this, 'sat-app') as SatApp | null;
    const viewer = app?.getGlobe()?.viewer;
    if (viewer === undefined) return;
    flyToAltitude(viewer, preset.altitudeM);
    this.menuOpen = false;
    // Optimistically reflect the new altitude so the active-preset
    // highlight updates immediately rather than waiting for the
    // 500 ms poll tick.
    this.cameraAltitudeM = preset.altitudeM;
  }

  render() {
    // Sky view hides the button entirely (§ II row 10).
    if (this.viewMode === 'sky') return nothing;
    const action = this.currentAction();
    const glyph = this.glyphFor(action);
    const label = this.labelFor(action);
    const tooltip = this.tooltipFor(action);
    const showMenu = action === 'in-no-sel' && this.menuOpen && !this.inReplayRoute;
    const activeKey = action === 'in-no-sel'
      ? findClosestAltitudePreset(this.cameraAltitudeM)
      : null;
    return html`
      <button
        type="button"
        @click=${this.handleClick}
        ?disabled=${this.inReplayRoute}
        title=${tooltip}
        aria-label=${tooltip}
        aria-haspopup=${action === 'in-no-sel' ? 'menu' : 'false'}
        aria-expanded=${action === 'in-no-sel' ? (this.menuOpen ? 'true' : 'false') : 'false'}
        data-action=${action}
      >
        <span class="toggle__glyph" aria-hidden="true">${glyph}</span>
        <span class="toggle__label">${label}</span>
      </button>
      ${showMenu ? html`
        <div class="menu" role="menu" aria-label="Altitude presets">
          <h3>Camera altitude</h3>
          ${ALTITUDE_PRESETS.map((p) => html`
            <button
              type="button"
              role="menuitem"
              class="item"
              data-active=${p.key === activeKey ? 'true' : 'false'}
              data-preset=${p.key}
              @click=${() => this.handleAltitudeClick(p)}
            >
              <span class="item__label">${p.label}</span>
              <span class="item__desc">${p.description}</span>
            </button>
          `)}
        </div>
      ` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sat-zoom-button': SatZoomButton;
  }
}
